/**
 * Formatação pt-BR (RNF-111).
 *
 * A API devolve **ISO-8601 em UTC** e recebe filtro de data em `YYYY-MM-DD`
 * interpretado como dia civil de **America/Sao_Paulo** (RN-55). A conversão entre
 * os dois mundos é toda daqui: espalhar `new Date().toISOString().slice(0,10)`
 * pelas telas produziria filtro deslocado em um dia para quem usa o sistema
 * depois das 21h — que é exatamente o horário em que o corretor lança o
 * atendimento do fim do dia.
 */

export const FUSO_OPERACIONAL = 'America/Sao_Paulo'

const formatadorDataHora = new Intl.DateTimeFormat('pt-BR', {
  timeZone: FUSO_OPERACIONAL,
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
})

const formatadorData = new Intl.DateTimeFormat('pt-BR', {
  timeZone: FUSO_OPERACIONAL,
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
})

const formatadorHora = new Intl.DateTimeFormat('pt-BR', {
  timeZone: FUSO_OPERACIONAL,
  hour: '2-digit',
  minute: '2-digit',
})

const formatadorDiaSemana = new Intl.DateTimeFormat('pt-BR', {
  timeZone: FUSO_OPERACIONAL,
  weekday: 'short',
  day: '2-digit',
  month: 'short',
})

function paraData(valor: string | Date | null | undefined): Date | null {
  if (!valor) return null
  const data = valor instanceof Date ? valor : new Date(valor)
  return Number.isNaN(data.getTime()) ? null : data
}

export function formatarDataHora(valor: string | Date | null | undefined, vazio = '—'): string {
  const data = paraData(valor)
  return data ? formatadorDataHora.format(data) : vazio
}

export function formatarData(valor: string | Date | null | undefined, vazio = '—'): string {
  const data = paraData(valor)
  return data ? formatadorData.format(data) : vazio
}

export function formatarHora(valor: string | Date | null | undefined, vazio = '—'): string {
  const data = paraData(valor)
  return data ? formatadorHora.format(data) : vazio
}

export function formatarDiaSemana(valor: string | Date | null | undefined, vazio = '—'): string {
  const data = paraData(valor)
  return data ? formatadorDiaSemana.format(data) : vazio
}

/**
 * "há 2 h", "em 3 dias". A idade do lead é a informação que o painel usa para
 * priorizar, e "10/08/2026 09:12" exige que o operador faça a conta de cabeça.
 */
export function formatarTempoRelativo(
  valor: string | Date | null | undefined,
  agora: Date = new Date(),
): string {
  const data = paraData(valor)
  if (!data) return '—'

  const diferencaSegundos = Math.round((data.getTime() - agora.getTime()) / 1000)
  const absoluto = Math.abs(diferencaSegundos)

  const formatador = new Intl.RelativeTimeFormat('pt-BR', { numeric: 'auto', style: 'short' })

  if (absoluto < 60) return formatador.format(diferencaSegundos, 'second')
  if (absoluto < 3600) return formatador.format(Math.round(diferencaSegundos / 60), 'minute')
  if (absoluto < 86_400) return formatador.format(Math.round(diferencaSegundos / 3600), 'hour')
  if (absoluto < 2_592_000) return formatador.format(Math.round(diferencaSegundos / 86_400), 'day')
  if (absoluto < 31_536_000)
    return formatador.format(Math.round(diferencaSegundos / 2_592_000), 'month')
  return formatador.format(Math.round(diferencaSegundos / 31_536_000), 'year')
}

/** `YYYY-MM-DD` no **dia civil de São Paulo** — o formato de filtro do contrato. */
export function paraDataFiltro(valor: Date): string {
  const partes = new Intl.DateTimeFormat('en-CA', {
    timeZone: FUSO_OPERACIONAL,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(valor)
  return partes
}

export function hojeFiltro(): string {
  return paraDataFiltro(new Date())
}

export function diasAtras(dias: number): string {
  const data = new Date()
  data.setDate(data.getDate() - dias)
  return paraDataFiltro(data)
}

/**
 * Converte o valor de um `<input type="datetime-local">` (que é hora local do
 * navegador, sem fuso) para o ISO-8601 com offset que a API espera.
 *
 * O caminho é o construtor `Date` sobre a string local, e não concatenar `:00Z`:
 * concatenar assumiria que o operador digitou UTC e gravaria a visita das 14h
 * como 11h.
 */
export function localParaInstante(valorLocal: string): string | null {
  if (!valorLocal) return null
  const data = new Date(valorLocal)
  return Number.isNaN(data.getTime()) ? null : data.toISOString()
}

/** Caminho inverso: ISO da API → valor de `datetime-local`. */
export function instanteParaLocal(valor: string | null | undefined): string {
  const data = paraData(valor)
  if (!data) return ''
  const deslocado = new Date(data.getTime() - data.getTimezoneOffset() * 60_000)
  return deslocado.toISOString().slice(0, 16)
}

/**
 * Telefone E.164 → máscara brasileira. `+5562999000001` → `(62) 99900-0001`.
 *
 * A coluna guarda E.164 (RN-12) e a operação lê no formato local; exibir o E.164
 * cru faz o corretor conferir dígito por dígito ao ditar o número.
 */
export function formatarTelefone(valor: string | null | undefined): string {
  if (!valor) return '—'
  const digitos = valor.replace(/\D/g, '')

  if (digitos.startsWith('55') && (digitos.length === 13 || digitos.length === 12)) {
    const ddd = digitos.slice(2, 4)
    const resto = digitos.slice(4)
    const meio = resto.length === 9 ? resto.slice(0, 5) : resto.slice(0, 4)
    const fim = resto.length === 9 ? resto.slice(5) : resto.slice(4)
    return `(${ddd}) ${meio}-${fim}`
  }

  // Número de outro país (ou incompleto): devolve como veio, com o `+`.
  return valor.startsWith('+') ? valor : `+${digitos}`
}

/** Só os dígitos — é o que a busca da API compara (`04-leads-workflows.md §11`). */
export function apenasDigitos(valor: string): string {
  return valor.replace(/\D/g, '')
}

/** Dinheiro chega como string decimal (`"350000.00"`), nunca `number` (§0). */
export function formatarDinheiro(valor: string | null | undefined, vazio = '—'): string {
  if (valor === null || valor === undefined || valor === '') return vazio
  const numero = Number(valor)
  if (Number.isNaN(numero)) return vazio
  return numero.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
}

export function formatarNumero(valor: number | null | undefined, vazio = '—'): string {
  if (valor === null || valor === undefined) return vazio
  return valor.toLocaleString('pt-BR')
}

/**
 * Duração curta e legível — `32s`, `2m 14s`, `1h 12m`, `3d 04h`.
 *
 * É o formato do tempo médio de primeira resposta da TELA-01. Mostrar `542.34
 * segundos` obrigaria o gestor a dividir por 60 de cabeça para comparar dois
 * corretores, que é justamente a leitura que o ranking existe para dar de graça.
 *
 * Sempre **duas** unidades, nunca três: a segunda casa é o que separa 4m10s de
 * 4m55s, e a terceira já não muda decisão nenhuma. A unidade menor vem com zero à
 * esquerda (`2h 05m`) para as linhas ficarem alinhadas na coluna tabular.
 *
 * Dias existem porque a base tem: lead que chega sexta à noite e é atendido na
 * segunda produz `3d 04h`, e `76h 12m` não se lê.
 */
export function formatarDuracaoCurta(segundos: number | null | undefined, vazio = '—'): string {
  if (segundos === null || segundos === undefined || !Number.isFinite(segundos)) return vazio

  const total = Math.max(0, Math.round(segundos))

  if (total < 60) return `${total}s`

  if (total < 3_600) {
    const minutos = Math.floor(total / 60)
    return `${minutos}m ${String(total % 60).padStart(2, '0')}s`
  }

  if (total < 86_400) {
    const horas = Math.floor(total / 3_600)
    return `${horas}h ${String(Math.floor((total % 3_600) / 60)).padStart(2, '0')}m`
  }

  const dias = Math.floor(total / 86_400)
  return `${dias}d ${String(Math.floor((total % 86_400) / 3_600)).padStart(2, '0')}h`
}

export function formatarPercentual(valor: number | null | undefined, casas = 1): string {
  if (valor === null || valor === undefined) return '—'
  return `${valor.toLocaleString('pt-BR', { maximumFractionDigits: casas })}%`
}

export function formatarCpfCnpj(valor: string | null | undefined): string {
  if (!valor) return '—'
  const digitos = valor.replace(/\D/g, '')
  if (digitos.length === 11) {
    return digitos.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4')
  }
  if (digitos.length === 14) {
    return digitos.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5')
  }
  return valor
}

export function formatarCep(valor: string | null | undefined): string {
  if (!valor) return '—'
  const digitos = valor.replace(/\D/g, '')
  return digitos.length === 8 ? digitos.replace(/(\d{5})(\d{3})/, '$1-$2') : valor
}

/** Primeiro nome — para saudação e para caber no badge de responsável. */
export function primeiroNome(nome: string): string {
  return nome.trim().split(/\s+/)[0] ?? nome
}
